import React, { useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * A real in-app screen recording shown inside the product UI.
 *
 * Browsers only allow autoplay when muted, so the clip starts muted + looping
 * (alive instantly) and exposes a "tap for sound" toggle. Tapping unmutes and
 * ensures playback — the only reliable way to play audio on the web without a
 * user gesture breaking autoplay.
 */
export const AppVideo = ({
  sources = [],
  poster,
  label,
  rounded = "34px",
  className = "",
  soundLabel = "Tap for sound",
  island = false,
}) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const v = ref.current;
    const next = !muted;
    setMuted(next);
    if (v && !next) {
      // ensure it is actually playing when the user asks for sound
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  };

  return (
    <div className={`relative ${className}`}>
      <video
        ref={ref}
        className="w-full block"
        style={{ borderRadius: rounded }}
        poster={poster}
        autoPlay
        muted={muted}
        loop
        playsInline
        preload="metadata"
        aria-label={label}
      >
        {sources.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
      </video>

      {island && <span className="dynamic-island" aria-hidden="true" />}

      <button
        type="button"
        onClick={toggleSound}
        aria-label={muted ? `${soundLabel}` : "Mute"}
        title={muted ? soundLabel : "Mute"}
        className="absolute bottom-3 right-3 z-30 flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-2 text-white backdrop-blur-md transition hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        {muted && (
          <span className="text-[0.7rem] font-medium uppercase tracking-wide hidden sm:inline">
            {soundLabel}
          </span>
        )}
      </button>
    </div>
  );
};

export default AppVideo;
